import {
  Box,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import moment from 'moment/moment'
import lightTheme from '../../styles/theme/lightTheme'
import * as React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEye,
  faEllipsisVertical,
  faBriefcase,
} from '@fortawesome/pro-regular-svg-icons'
import AppPagination from '../paginations'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

const TablePanelBox = styled(TabPanel)(({ theme }) => ({
  height: 'calc(100% - 45px)',
  marginBottom: 10,
  backgroundColor: 'rgb(227, 230, 255, 0.5)',
  '& .MuiBox-root': {
    padding: 0,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  whiteSpace: 'nowrap',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  verticalAlign: 'top',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const ButtonIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  border: `1px solid ${lightTheme.palette.primary.b2}`,
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  margin: '0 4px',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    border: `2px solid ${lightTheme.palette.primary.b2}`,
    backgroundColor: 'rgba(102, 126, 234, 0.06)',
  },
}))

const StyledActionMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 16,
    minWidth: 220,
    border: '1px solid rgba(99, 102, 241, 0.15)',
    boxShadow: '0 24px 60px rgba(99, 102, 241, 0.18)',
  },
}))

const StyledActionMenuItem = styled(MenuItem)(({ theme }) => ({
  px: 2.5,
  py: 1.25,
  borderRadius: 12,
  gap: 10,
  color: lightTheme.palette.text.primary,
  fontFamily: 'Century Gothic',
  fontSize: 14,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  '& .menuActionIcon': {
    minWidth: 24,
    color: '#667eea',
  },
}))

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ status }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  height: 24,
  borderRadius: '8px',
  ...(status === 'postule' && {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    color: '#e65100',
  }),
  ...(status === 'accepte' && {
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
    color: '#2e7d32',
  }),
  ...(status === 'refuse' && {
    backgroundColor: 'rgba(244, 67, 54, 0.12)',
    color: '#c62828',
  }),
  ...(status === 'entretien' && {
    backgroundColor: 'rgba(33, 150, 243, 0.12)',
    color: '#1565c0',
  }),
}))

const getJobTitle = (candidature, language) =>
  language === 'fr'
    ? candidature.besoinEntreprise?.poste?.titre
    : candidature.besoinEntreprise?.poste?.titreEn ||
      candidature.besoinEntreprise?.poste?.titre

const CandidatureTable = ({
  candidatures,
  loading,
  handleDetailCandidature,
  handlePageChange,
  count,
  page,
  userData,
}) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [menuCandidature, setMenuCandidature] = React.useState(null)

  const getStatusLabel = (status) => {
    switch (status) {
      case 'postule':
        return t('profile.applied')
      case 'accepte':
        return t('profile.accepted')
      case 'refuse':
        return t('profile.rejected')
      case 'entretien':
        return t('profile.interview')
      default:
        return status
    }
  }

  const handleMenuOpen = (event, candidature) => {
    setAnchorEl(event.currentTarget)
    setMenuCandidature(candidature)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuCandidature(null)
  }

  const handleDetail = (candidature) => {
    if (handleDetailCandidature) {
      handleDetailCandidature(candidature)
    }
    handleMenuClose()
  }

  const handleViewOffer = (candidature) => {
    navigate(`/offres-emploi/${candidature.besoinEntreprise.id}`)
    handleMenuClose()
  }

  const renderStatus = (candidature, size = 'small') => (
    <StatusChip
      size={size}
      status={candidature.status}
      label={getStatusLabel(candidature.status)}
    />
  )

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          minHeight: 120,
          py: 4,
        }}
      >
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    )
  }

  const hasItems = candidatures && candidatures.length > 0

  return (
    <TablePanelBox value={0} index={0}>
      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table
          stickyHeader
          aria-label="candidatures table"
          sx={{ minWidth: { xs: 280, sm: 'auto' } }}
        >
          <HeaderTab>
            <TableRow>
              <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                #
              </TitleHead>
              <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('profile.status')}
              </TitleHead>
              <TitleHead sx={{ minWidth: { xs: 160, sm: 'auto' } }}>
                {t('profile.jobPosition')}
              </TitleHead>
              <TitleHead sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {t('profile.date')}
              </TitleHead>
              <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('profile.interviewDate')}
              </TitleHead>
              <TitleHead
                sx={{
                  width: { xs: 56, sm: 140 },
                  textAlign: 'center',
                }}
              >
                {t('profile.actions')}
              </TitleHead>
            </TableRow>
          </HeaderTab>
          <BodyTable>
            {hasItems ? (
              candidatures.map((candidature) => (
                <TableRow key={candidature.id}>
                  <StyledTableCell
                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: '#6b7280' }}
                    >
                      #{candidature.id}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                  >
                    {renderStatus(candidature)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: { xs: 160, sm: 'auto' } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="body2"
                        onClick={() => handleDetail(candidature)}
                        sx={{
                          color: '#667eea',
                          cursor: 'pointer',
                          fontWeight: 600,
                          lineHeight: 1.35,
                          '&:hover': {
                            color: '#5a67d8',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {getJobTitle(candidature, i18n.language)}
                      </Typography>

                      <Box
                        sx={{
                          display: { xs: 'flex', md: 'none' },
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            ID:
                          </Typography>
                          <Typography variant="caption">
                            #{candidature.id}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            {t('profile.date')}:
                          </Typography>
                          <Typography variant="caption">
                            {moment(candidature.date).format('DD/MM/YYYY')}
                          </Typography>
                        </Box>
                        {candidature.dateEntretien && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              {t('profile.interviewDate')}:
                            </Typography>
                            <Typography variant="caption">
                              {moment(candidature.dateEntretien).format(
                                'DD/MM/YYYY HH:mm'
                              )}
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {renderStatus(candidature, 'small')}
                        </Box>
                      </Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ display: { xs: 'none', md: 'table-cell' } }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {moment(candidature.date).format('DD/MM/YYYY')}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {candidature.dateEntretien
                        ? moment(candidature.dateEntretien).format(
                            'DD/MM/YYYY HH:mm'
                          )
                        : '—'}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box
                      sx={{
                        display: { xs: 'flex', sm: 'none' },
                        justifyContent: 'center',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, candidature)}
                        aria-label={t('profile.actions')}
                      >
                        <FontAwesomeIcon
                          icon={faEllipsisVertical}
                          color={lightTheme.palette.primary.b2}
                          fontSize={16}
                        />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={t('header.offer')} arrow>
                        <ButtonIcon
                          onClick={() => handleViewOffer(candidature)}
                        >
                          <FontAwesomeIcon
                            icon={faBriefcase}
                            fontSize={15}
                            color={lightTheme.palette.primary.b2}
                          />
                        </ButtonIcon>
                      </Tooltip>
                      <Tooltip
                        title={`${t('button.view')} ${t(
                          'profile.application'
                        )}`}
                        arrow
                      >
                        <ButtonIcon onClick={() => handleDetail(candidature)}>
                          <FontAwesomeIcon
                            icon={faEye}
                            fontSize={16}
                            color={lightTheme.palette.primary.b2}
                          />
                        </ButtonIcon>
                      </Tooltip>
                    </Box>

                    <StyledActionMenu
                      anchorEl={anchorEl}
                      open={
                        Boolean(anchorEl) && menuCandidature === candidature
                      }
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      PaperProps={{
                        sx: {
                          p: 1,
                          bgcolor: '#fff',
                        },
                      }}
                    >
                      <StyledActionMenuItem
                        onClick={() => handleViewOffer(candidature)}
                      >
                        <FontAwesomeIcon
                          icon={faBriefcase}
                          className="menuActionIcon"
                          fontSize={14}
                        />
                        <Typography variant="body2">
                          {t('header.offer')}
                        </Typography>
                      </StyledActionMenuItem>
                      <StyledActionMenuItem
                        onClick={() => handleDetail(candidature)}
                      >
                        <FontAwesomeIcon
                          icon={faEye}
                          className="menuActionIcon"
                          fontSize={14}
                        />
                        <Typography variant="body2">
                          {t('button.view')} {t('profile.application')}
                        </Typography>
                      </StyledActionMenuItem>
                    </StyledActionMenu>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2">
                    {t('profile.noApplications')}
                  </Typography>
                </StyledTableCell>
              </TableRow>
            )}
          </BodyTable>
        </Table>
      </Box>

      {count > 10 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
            px: { xs: 1, sm: 0 },
          }}
        >
          <AppPagination
            count={count}
            page={page}
            onChange={handlePageChange}
          />
        </Box>
      )}
    </TablePanelBox>
  )
}

CandidatureTable.propTypes = {
  candidatures: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  handleDetailCandidature: PropTypes.func,
  handlePageChange: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  userData: PropTypes.object,
}

export default CandidatureTable
